import React from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { NewsStory, Paper, OpenalexWork } from 'js/social/shared/types';
import ImpressionWatcher, {
  Props as ImpressionProps,
} from 'js/utils/impressions/ImpressionWatcher';
import PaperAbstract from './PaperAbstract';

const Container = styled.div<{ variant?: 'default' | 'dense' }>`
  word-break: break-word;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  align-self: stretch;

  gap: var(--spacing-sm-32, 32px);

  background: var(--feed-news-finding-card-inner-cntr-Color, #dcf2f6);
  padding: var(--spacing-sm-24, 24px);

  &.variant-dense {
    background: #fff;
    padding: 0;
  }

  @include for-tablet-sm-up {
    background: var(--feed-news-finding-card-inner-cntr-Color, #dcf2f6);
    padding: var(--spacing-sm-32, 32px);
  }
`;

const ContentAndButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: var(--spacing-xs-8, 8px);
  display: inline-block;
`;

const ContentContainer = styled.div<{ variant: 'default' | 'dense' }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;
  gap: var(--spacing-xs-8, 8px);
  margin-bottom: var(--spacing-xs-16, 16px);
  &.variant-dense {
    margin-bottom: 12px;
  }
`;

const Heading = styled(Body)<{ variant: 'default' | 'dense' }>`
  font-weight: 800;
  margin: 0 0 8px 0;
  &.variant-dense {
    font-family: var(--type-font-family-sans-serif, 'DM Sans');
    font-weight: 600;
    margin-bottom: 4px;
  }
`;

const NewsBody = styled(Body)<{ variant: 'default' | 'dense' }>`
  &.variant-dense {
    font-family: var(--type-font-family-sans-serif, 'DM Sans');
    font-weight: 400;
    margin: 0;
  }
`;

const WorkCardContainer = styled.div`
  & > * {
    border-radius: var(--border-radius-radius-md);
  }
`;

type Props = {
  newsStory: NewsStory;
  work: Paper | OpenalexWork;
  canUseReader: boolean;
  showReader: () => void;
  impressionPropsGen: (clickTarget?: string) => ImpressionProps;
  paperInteractions: React.ReactNode;
  variant?: 'default' | 'dense';
};

const PaperStoryContainer: React.FunctionComponent<Props> = ({
  newsStory,
  work,
  canUseReader,
  showReader,
  impressionPropsGen,
  paperInteractions,
  variant = 'default',
}) => {
  const { heading, body } = newsStory;

  const content = (
    <>
      <ContentAndButtonsContainer>
        <ContentContainer variant={variant}>
          <ImpressionWatcher {...impressionPropsGen('story-body')}>
            {heading && (
              <Heading size="md" variant={variant}>
                {heading}
              </Heading>
            )}
            {body && (
              <NewsBody size="md" variant={variant}>
                {body}
              </NewsBody>
            )}
          </ImpressionWatcher>
        </ContentContainer>
        <WorkCardContainer>
          <PaperAbstract
            work={work}
            canUseReader={canUseReader}
            showReader={showReader}
            impressionPropsGen={impressionPropsGen}
            paperInteractions={null}
            size="sm"
            isSnippet={!!newsStory}
            variant={variant}
          />
        </WorkCardContainer>
      </ContentAndButtonsContainer>
    </>
  );

  return (
    <Container variant={variant}>
      {content}
      {paperInteractions}
    </Container>
  );
};

export default PaperStoryContainer;
